import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { SkeletonCards } from '../components/ui/Skeleton';
import Modal from '../components/ui/Modal';
import {
  Briefcase,
  Building2,
  FileText,
  TrendingUp,
  Zap,
  MapPin,
  Clock,
  ArrowUpRight,
  RefreshCw,
  Target,
  Star,
  ExternalLink
} from 'lucide-react';

interface OverviewStats {
  jobs: number;
  companies: number;
  resumes: number;
  analyses: number;
  executive_jobs: number;
  senior_jobs: number;
  remote_jobs: number;
  open_jobs: number;
  closed_jobs: number;
  recent_scrapes: Array<{ company_name: string; status: string; jobs_found: number; created_at: string }>;
}

interface RecentJob {
  id: number;
  title: string;
  company_name: string;
  location: string;
  job_type: string;
  experience_level: string;
  scraped_at: string;
  application_url: string | null;
  description: string | null;
  description_plain: string | null;
  status: string;
  last_seen_at: string | null;
}

interface TopMatch {
  job_id: number;
  job_title: string;
  company_name: string;
  location: string;
  overall_score: number;
  skill_score: number;
  semantic_similarity: number;
  matched_skills: string[];
  missing_skills: string[];
}

function stripHtml(html: string): string {
  // Pass 1: decode HTML entities (turns &lt;h2&gt; into <h2>)
  const decoded = new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
  // Pass 2: strip actual HTML tags (turns <h2>text</h2> into text)
  return new DOMParser().parseFromString(decoded, 'text/html').body.textContent || '';
}

const Dashboard: React.FC = () => {
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [scraping, setScraping] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [topMatches, setTopMatches] = useState<TopMatch[]>([]);
  const [selectedJob, setSelectedJob] = useState<RecentJob | null>(null);
  const [detailJob, setDetailJob] = useState<Record<string, unknown> | null>(null);
  const { toast } = useToast();
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    // Loaded independently so one failure doesn't blank the other section.
    const loadOverview = api
      .get<OverviewStats>('/api/stats/overview', { retry: 2 })
      .then((d) => { if (active) setOverview(d); })
      .catch(() => {});
    const loadRecent = api
      .get<{ data?: RecentJob[] }>('/api/jobs?limit=5&sort_by=scraped_at&sort_order=DESC', { retry: 2 })
      .then((d) => { if (active) setRecentJobs(d.data || []); })
      .catch(() => {});
    Promise.all([loadOverview, loadRecent]).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const startScraping = async () => {
    setScraping(true);
    try {
      await api.post('/api/scrape/all', {});
      // Refresh data after scraping
      const [statsRes, jobsRes] = await Promise.all([
        fetch(`${API_BASE}/api/stats/overview`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/jobs?limit=5&sort_by=scraped_at&sort_order=DESC`, { credentials: 'include' }),
      ]);
      setOverview(await statsRes.json());
      const jobsData = await jobsRes.json();
      setRecentJobs(jobsData.data || []);
    } catch (e) {
      toast.error(errorText(e, 'Scraping failed'));
    } finally {
      setScraping(false);
    }
  };

  const startScoring = async () => {
    setScoring(true);
    try {
      const data = await api.post<{ top_matches?: TopMatch[] }>('/api/matching/score');
      setTopMatches(data.top_matches || []);
    } catch (e) {
      toast.error(errorText(e, 'Job scoring failed'));
    } finally {
      setScoring(false);
    }
  };

  const openJobDetail = (job: RecentJob) => {
    setSelectedJob(job);
    setDetailJob(null);
    setLoadingDetail(true);
    fetch(`${API_BASE}/api/jobs/${job.id}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => setDetailJob(data))
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  };

  const openMatchDetail = (match: TopMatch) => {
    setSelectedJob(null);
    setDetailJob(null);
    setLoadingDetail(true);
    fetch(`${API_BASE}/api/jobs/${match.job_id}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        const d = data as Record<string, unknown>;
        setSelectedJob({
          id: match.job_id,
          title: match.job_title,
          company_name: match.company_name,
          location: match.location,
          job_type: '',
          experience_level: '',
          scraped_at: '',
          application_url: d.application_url as string | null,
          description: d.description as string | null,
          description_plain: d.description_plain as string | null,
          status: (d.status as string) || 'open',
          last_seen_at: (d.last_seen_at as string) || null,
        });
        setDetailJob(data);
      })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  };

  const closeModal = () => {
    setSelectedJob(null);
    setDetailJob(null);
  };

  const matchScoreColor = (score: number) => {
    const pct = Math.round(score * 100);
    if (pct >= 80) return 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/40';
    if (pct >= 60) return 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/40';
    return 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/40';
  };

  const stats = [
    {
      name: 'Total Jobs',
      value: overview?.jobs ?? 0,
      detail: `${overview?.open_jobs ?? 0} open · ${overview?.closed_jobs ?? 0} closed`,
      icon: Briefcase,
      color: 'primary'
    },
    {
      name: 'Active Companies',
      value: overview?.companies ?? 0,
      detail: 'tracked companies',
      icon: Building2,
      color: 'success'
    },
    {
      name: 'Resumes Uploaded',
      value: overview?.resumes ?? 0,
      detail: 'ready for analysis',
      icon: FileText,
      color: 'warning'
    },
    {
      name: 'Analyses Completed',
      value: overview?.analyses ?? 0,
      detail: 'match analyses',
      icon: TrendingUp,
      color: 'secondary'
    }
  ];

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr + 'Z').getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-slate-400 mt-1">Welcome back! Here's what's happening with your job search.</p>
        </div>
        <div className="flex space-x-3">
          <motion.button
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startScoring}
            disabled={scoring}
          >
            {scoring ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Target className="w-4 h-4 mr-2" />}
            {scoring ? 'Scoring...' : 'Score Jobs'}
          </motion.button>
          <motion.button
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startScraping}
            disabled={scraping}
          >
            {scraping ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            {scraping ? 'Scraping...' : 'Start Scraping'}
          </motion.button>
        </div>
      </div>

      {/* Stats Grid */}
      {loading && !overview ? (
        <SkeletonCards count={4} className="grid-cols-1 md:grid-cols-2 lg:grid-cols-4" />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.name}
              className="bg-white dark:bg-navy-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-slate-400">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{(stat.value ?? 0).toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/30">
                  <Icon className="w-6 h-6 text-primary-600" />
                </div>
              </div>
              <div className="flex items-center mt-4">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-500 dark:text-slate-400 ml-1">{stat.detail}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Jobs */}
        <div className="lg:col-span-2">
          <motion.div
            className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-800">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent Job Postings</h3>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Latest opportunities from your tracked companies</p>
            </div>
            <div className="p-6">
              {recentJobs.length === 0 ? (
                <p className="text-gray-500 dark:text-slate-400 text-center py-4">No jobs yet. Start scraping to discover opportunities!</p>
              ) : (
                <div className="space-y-4">
                  {recentJobs.map((job) => (
                    <div
                      key={job.id}
                      className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-800 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors duration-200 cursor-pointer"
                      onClick={() => openJobDetail(job)}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">{job.title}</h4>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500 dark:text-slate-400">
                          <span className="flex items-center">
                            <Building2 className="w-4 h-4 mr-1" />
                            {job.company_name}
                          </span>
                          <span className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {job.location}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {timeAgo(job.scraped_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          job.status === 'open' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400'
                        }`}>
                          {job.status === 'open' ? 'Open' : 'Closed'}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          job.experience_level === 'Executive' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-400' :
                          job.experience_level === 'Senior' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-400' :
                          'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {job.experience_level}
                        </span>
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300">
                          {job.job_type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-800">
                <Link to="/jobs" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                  View all jobs →
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <motion.div
            className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-800">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Quick Actions</h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <motion.button
                  className="w-full flex items-center space-x-3 p-3 text-left rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors duration-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={startScraping}
                  disabled={scraping}
                >
                  <div className="p-2 rounded-md bg-primary-100 dark:bg-primary-900/30">
                    <Zap className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Start Job Scraping</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Crawl all tracked companies</p>
                  </div>
                </motion.button>
                <Link to="/resumes">
                  <motion.button
                    className="w-full flex items-center space-x-3 p-3 text-left rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors duration-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="p-2 rounded-md bg-green-100 dark:bg-green-900/30">
                      <FileText className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Upload Resume</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">Add a new resume for analysis</p>
                    </div>
                  </motion.button>
                </Link>
                <Link to="/analytics">
                  <motion.button
                    className="w-full flex items-center space-x-3 p-3 text-left rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors duration-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="p-2 rounded-md bg-yellow-100 dark:bg-yellow-900/30">
                      <TrendingUp className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">View Analytics</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">AI-powered job market insights</p>
                    </div>
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Recent Scraping Activity */}
          <motion.div
            className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-800">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent Activity</h3>
            </div>
            <div className="p-6">
              {!overview?.recent_scrapes?.length ? (
                <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-2">No activity yet</p>
              ) : (
                <div className="space-y-3">
                  {overview.recent_scrapes.map((scrape, i) => (
                    <div key={i} className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${scrape.status === 'success' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {scrape.company_name}: {scrape.jobs_found} jobs found
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">{timeAgo(scrape.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Top Matches */}
      {topMatches.length > 0 && (
        <motion.div
          className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="p-6 border-b border-gray-200 dark:border-slate-800">
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Top Job Matches ({topMatches.length})</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Based on your profile, skills, and preferences</p>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-slate-800">
            {topMatches.slice(0, 10).map((match, i) => (
              <div key={match.job_id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer" onClick={() => openMatchDetail(match)}>
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-400 dark:text-slate-500 w-6">#{i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{match.job_title}</p>
                    <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500 dark:text-slate-400">
                      <span className="flex items-center">
                        <Building2 className="w-3 h-3 mr-1" />{match.company_name}
                      </span>
                      {match.location && (
                        <span className="flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />{match.location}
                        </span>
                      )}
                    </div>
                    {match.matched_skills.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {match.matched_skills.slice(0, 5).map((s) => (
                          <span key={s} className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 rounded">{s}</span>
                        ))}
                        {match.matched_skills.length > 5 && (
                          <span className="text-[10px] text-gray-400 dark:text-slate-500">+{match.matched_skills.length - 5}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <span className={`px-3 py-1 text-sm font-bold rounded-full ${matchScoreColor(match.overall_score)}`}>
                  {Math.round(match.overall_score * 100)}%
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Job Detail Modal */}
      <Modal
        isOpen={!!selectedJob}
        onClose={closeModal}
        title={selectedJob?.title || 'Job details'}
        size="lg"
      >
        {selectedJob && (
          <>
            <p className="mb-4 flex items-center text-gray-600 dark:text-slate-400">
              <Building2 className="w-4 h-4 mr-1" />
              {selectedJob.company_name}
              {selectedJob.location && (
                <span className="ml-3 flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />{selectedJob.location}
                </span>
              )}
            </p>

            {loadingDetail ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 text-gray-400 dark:text-slate-500 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-slate-400">Loading details...</p>
              </div>
            ) : detailJob ? (
              <div className="space-y-4">
                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  {Boolean(detailJob.status) && (
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                      detailJob.status === 'open' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400'
                    }`}>
                      {detailJob.status === 'open' ? 'Open' : 'Closed'}
                    </span>
                  )}
                  {Boolean(detailJob.experience_level) && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-400">
                      {String(detailJob.experience_level)}
                    </span>
                  )}
                  {Boolean(detailJob.job_type) && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300">
                      {String(detailJob.job_type)}
                    </span>
                  )}
                  {Boolean(detailJob.remote_policy) && detailJob.remote_policy !== 'unknown' && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400">
                      {String(detailJob.remote_policy)}
                    </span>
                  )}
                  {Boolean(detailJob.salary_range) && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400">
                      {String(detailJob.salary_range)}
                    </span>
                  )}
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Description</h3>
                  {(() => {
                    const raw = String(detailJob.description_plain || detailJob.description || '');
                    const clean = raw ? stripHtml(raw) : '';
                    return clean ? (
                      <div className="text-sm text-gray-600 dark:text-slate-400 whitespace-pre-line max-h-64 overflow-y-auto leading-relaxed">
                        {clean.slice(0, 3000)}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-slate-400">No description available.</p>
                    );
                  })()}
                </div>

                {/* Apply button */}
                {Boolean(detailJob.application_url) && (
                  <a
                    href={String(detailJob.application_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Apply Now
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-slate-400">Could not load job details.</p>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
